<app-loader></app-loader>

<div class="codex-viewport">

  <!-- Ambient Burgundy Atmospheric Aurora -->
  <div class="codex-ambient-aurora" aria-hidden="true">
    <div class="aurora-orb aurora-orb--top"></div>
    <div class="aurora-orb aurora-orb--bottom"></div>
  </div>

  <div class="codex-container">

    <!-- Top: Sovereign Command Dock -->
    <app-profile-sidebar class="w-full mb-6"></app-profile-sidebar>

    <!-- Main Sovereign Codex Command Shell -->
    <main class="codex-shell">
      <div class="codex-foil-sheen" aria-hidden="true"></div>

      <!-- Header Section -->
      <header class="codex-header">
        <div class="header-main-lockup">
          <div class="seal-halo-icon">
            <i class="fas fa-file-shield text-[#E7CA96]"></i>
          </div>

          <div class="header-titles">
            <div class="header-tag-row">
              <span class="codex-tag">PLATFORM CONSTITUTION</span>
              <span class="codex-status-pip"><span class="pip-dot"></span> CODEX V4.2 ACTIVE</span>
            </div>
            <h1 class="codex-title">
              SOVEREIGN ROYAL <span class="title-gold">CODEX</span>
            </h1>
            <p class="codex-lead">
              Official protocols and legal covenants governing player citizenship, provably fair gaming engines, vault deposits, and automated cashier settlements.
            </p>
          </div>
        </div>

        <!-- Telemetry Stats Bar -->
        <div class="codex-telemetry-grid">
          <div class="telemetry-pill">
            <i class="fas fa-shield-halved text-[#10B981]"></i>
            <div class="telemetry-meta">
              <span class="telemetry-val">256-BIT SSL</span>
              <span class="telemetry-lbl">Vault Cipher</span>
            </div>
          </div>

          <div class="telemetry-pill">
            <i class="fas fa-dice text-[#E7CA96]"></i>
            <div class="telemetry-meta">
              <span class="telemetry-val">100% FAIR</span>
              <span class="telemetry-lbl">Certified RNG</span>
            </div>
          </div>

          <div class="telemetry-pill">
            <i class="fas fa-bolt text-[#E7CA96]"></i>
            <div class="telemetry-meta">
              <span class="telemetry-val">AUTOMATED</span>
              <span class="telemetry-lbl">Fast Payouts</span>
            </div>
          </div>

          <div class="telemetry-pill">
            <i class="fas fa-user-check text-[#F6E9D5]"></i>
            <div class="telemetry-meta">
              <span class="telemetry-val">18+ ONLY</span>
              <span class="telemetry-lbl">Verified Play</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Interactive Toolbar: Category Tabs + Quick Search -->
      <section class="codex-toolbar">
        <!-- Categories Horizontal Slider -->
        <div class="category-tabs-track">
          @for (cat of categories; track cat.key) {
          <button
            type="button"
            class="cat-tab-pill"
            [class.is-active]="activeCategory === cat.key"
            (click)="setCategory(cat.key)"
          >
            <i [class]="cat.icon + ' text-xs mr-2'"></i>
            <span>{{ cat.label }}</span>
          </button>
          }
        </div>

        <!-- Real-Time Search Field -->
        <div class="codex-search-wrapper">
          <i class="fas fa-search search-icon"></i>
          <input
            type="text"
            placeholder="Search protocols & terms..."
            [(ngModel)]="searchTerm"
            class="codex-search-input"
          />
          <button
            *ngIf="searchTerm"
            (click)="searchTerm = ''"
            class="clear-search-btn"
            title="Clear search"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </section>

      <!-- Protocol Cards Grid -->
      <section class="codex-matrix">
        @if (filteredProtocols.length > 0) {
          @for (proto of filteredProtocols; track proto.id) {
          <article
            class="codex-card"
            [class.is-expanded]="expandedProtocolId === proto.id"
            (click)="toggleExpand(proto.id)"
          >
            <div class="card-ambient-highlight"></div>

            <div class="card-top-row">
              <div class="card-left-badge">
                <span class="protocol-roman">{{ proto.roman }}</span>
                <span class="protocol-category-pill">{{ proto.badge }}</span>
              </div>
              <div class="card-icon-bubble">
                <i [class]="proto.icon"></i>
              </div>
            </div>

            <h3 class="card-title">{{ proto.title }}</h3>
            <p class="card-summary">{{ proto.summary }}</p>

            <!-- Expanded Deep-Dive Drawer -->
            @if (expandedProtocolId === proto.id) {
            <div class="card-deep-dive">
              <ul class="detail-bullets">
                @for (d of proto.details; track d) {
                <li>
                  <i class="fas fa-diamond text-[#E7CA96] text-[8px] mt-1 flex-shrink-0"></i>
                  <span>{{ d }}</span>
                </li>
                }
              </ul>
            </div>
            }

            <div class="card-footer-row">
              <span class="card-audit-tag">
                <i class="fas fa-circle-check text-emerald-400 mr-1.5"></i>
                {{ proto.auditBadge }}
              </span>

              <button type="button" class="expand-indicator-btn" aria-label="Expand details">
                <span>{{ expandedProtocolId === proto.id ? 'Collapse' : 'Details' }}</span>
                <i class="fas fa-chevron-down chevron-icon" [class.rotate-180]="expandedProtocolId === proto.id"></i>
              </button>
            </div>
          </article>
          }
        } @else {
          <div class="codex-empty-state">
            <i class="fas fa-folder-open text-3xl text-[#E7CA96] mb-3 opacity-60"></i>
            <h4 class="text-base font-bold text-[#F6E9D5]">No matching protocols found</h4>
            <p class="text-xs text-[#9C837C] mt-1">Try searching for keywords like "vault", "RNG", "redemption", or "security".</p>
            <button type="button" (click)="searchTerm = ''; activeCategory = 'all'" class="reset-filter-btn mt-4">
              Reset Filters
            </button>
          </div>
        }
      </section>

      <!-- Bottom Compliance & Resolution Dossier -->
      <section class="codex-dispute-dock">
        <div class="dispute-icon-chamber">
          <i class="fas fa-scale-balanced text-[#E7CA96]"></i>
        </div>
        <div class="dispute-content">
          <h4 class="dispute-title">DISPUTE ARBITRATION & SUPPORT ACCESS</h4>
          <p class="dispute-desc">
            Citizens who encounter discrepancies regarding balance adjustments, game results, or cashier settlements are encouraged to open a formal dossier through our 24/7 VIP Support Concierge.
          </p>
        </div>
        <button type="button" (click)="CloseModal()" class="dispute-cta-btn">
          <span>Return To Lobby</span>
          <i class="fas fa-arrow-right text-xs"></i>
        </button>
      </section>

    </main>

  </div>

</div>
